import type { AccountAddress } from '#types/components/account/address'
import type { LocaleCode } from '#types/locale'

type ValidatorName = 'required' | 'minLength' | 'maxLength' | 'exactLength' | 'pattern'

/**
 * @returns {{ load, validators, states, countries, formatters, getValidator }} API Rules validation methods and data
 */
export default () => {
  const addressRulesStore = useAddressRulesStore()
  const {
    validateMaxLength,
    validateMinLength,
    validateExactLength,
    validateRegex,
    validateRequired
  } = useLocalizedValidators()
  const { $t } = useNuxtApp()

  // localeKey holds the current country and locale
  const localeKey = ref('')
  // formKey holds the form type
  const formKey = ref<'billing' | 'shipping'>('billing')

  /**
   * Loads the validation rules from the API for the given country and locale
   * @param countryCode - The country code
   * @param locale - The locale
   * @param approachType - The approach type: either B or S (billing / shipping)
   */
  const load = (countryCode: string, locale: LocaleCode, approachType: AccountAddress['approachType'] = 'S') => {
    localeKey.value = `${countryCode.toLocaleLowerCase()}-${locale}`
    formKey.value = approachType === 'B' ? 'billing' : 'shipping'
    return addressRulesStore.get(countryCode, locale)
  }

  /**
   * Returns validators collection for the current locale and form type
   */
  const validators = computed(() => {
    if (!addressRulesStore.rules[localeKey.value]) return {}

    const rules = addressRulesStore.rules[localeKey.value][formKey.value]
    const validators_: Record<string, { [K in ValidatorName]?: [((...any) => Validator), ...any[]] }> = {}

    for (const field in rules) {
      const { required, pattern, minLength, maxLength } = (rules[field].validation || {})
      validators_[field] = {}
      if (required)
        validators_[field].required = [validateRequired, required, $t.validators.required]

      if (minLength && maxLength && minLength === maxLength) {
        validators_[field].exactLength = [
          validateExactLength,
          minLength,
          replaceAll($t.validators.exactLength, { size: minLength })
        ]
      }

      if (minLength) {
        validators_[field].minLength = [
          validateMinLength,
          minLength,
          replaceAll($t.validators.minLength, { min: minLength })
        ]
      }

      if (maxLength) {
        validators_[field].maxLength = [
          validateMaxLength,
          maxLength,
          replaceAll($t.validators.maxLength, { max: maxLength })
        ]
      }

      if (pattern) {
        let regex = new RegExp(pattern)
        const flags = pattern.match(/.*\/([gimyu]*)$/)?.[1]
        if (flags)
          regex = new RegExp(pattern.replace(new RegExp(`^/(.*?)/${flags}$`), '$1'), flags)
        validators_[field].pattern = [validateRegex, regex, $t.validation[field]]
      }
    }

    return validators_
  })

  const countries = computed(() => addressRulesStore.countries)

  const states = computed(() => (addressRulesStore.rules[localeKey.value]?.[formKey.value]?.province?.list || [])
    .map(({ name, code }) => ({ label: name, value: code })))

  const formatters = computed(() => {
    if (!addressRulesStore.rules[localeKey.value]) return {}
    const fields: Record<string, any> = addressRulesStore.rules[localeKey.value][formKey.value]
    return Object.values(fields).reduce((config, current) => {
      const { fieldId, format, transforms } = current || {}
      if (!format && !transforms) return config
      config[fieldId] = { fieldId, ...(format && { mask: format[0] }), ...(transforms && { transforms }) }
      return config
    }, {})
  })

  /**
   * Returns validators collection for a given field name
   * @param { string } field The field name
   * @param { string } label Field label translation to be used in error messaging for required fields
   */
  const getValidator = (field: string, label: string, translations: { [K in ValidatorName]?: string } = {}) =>
    validators.value[field]
      ? Object.entries(validators.value[field]).map(([name, [validator, ...args]]) => {
          const _translations = {
            ...(name === 'required') && { required: replaceAll($t.validators.required, { field: label }) },
            ...translations
          }
          const message = _translations[name] || args[args.length - 1]
          return validator(...args.slice(0, -1), message)
        })
      : []

  /**
   * Returns excludedCodes attribute for postalCode field.
   * @param { 'billing' | 'shipping' } form - Address form type (defaults to shipping)
   */
  const getPostalCodeExcludedCodes = (
    form: 'billing' | 'shipping' = 'shipping'
  ) => {
    const key = `${useCountryCode().toLocaleLowerCase()}-${useLocale()}`
    return addressRulesStore.rules[key][form].postalCode.excludedCodes || ''
  }

  return {
    countries,
    formatters,
    getPostalCodeExcludedCodes,
    getValidator,
    load,
    states,
    validators,
  }
}
